import { useNavigate } from 'react-router';
import { brandConfig, isTecalor } from '@common/brand/brandConfig';
import Box from '@common/components/Box';
import Button from '@common/components/controls/Button';
import Icon from '@common/components/Icon';
import PageFooterWrapper from '@common/components/PageFooter/PageFooterWrapper';
import IconFlag from '@common/components/svgs/common/Flag';
import IconMail from '@common/components/svgs/common/Mail';
import Translated from '@common/components/Translated';
import '@common/styles/components/pageFooter.pcss';

interface PageFooterProps {
  openTour?: () => void;
  openFeedback?: () => void;
  openSupport?: () => void;
  privacyPolicyHidden?: boolean;
}

function PageFooter({
  openTour,
  openFeedback,
  openSupport,
  privacyPolicyHidden,
}: Readonly<PageFooterProps>) {
  const navigate = useNavigate();

  const currentYear = new Date().getFullYear();
  return (
    <PageFooterWrapper>
      <div className="page-footer__content txt-small">
        <Box tagName="span">
          <Translated id="page-footer.copyright.with-year.companyName" values={{ currentYear }} />
        </Box>
        <div className="page-footer__links">
          {isTecalor && brandConfig.imprintUrl
            ? (
              <a
                href={brandConfig.imprintUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="imprint-button"
              >
                <Translated id="page-footer.imprint" tagName="strong" className="txt-small" />
              </a>
            )
            : (
              <Button
                type="button"
                mode="text-link"
                onClick={() => navigate('/imprint')}
                data-testid="imprint-button"
                disableAutoCapture
              >
                <Translated id="page-footer.imprint" tagName="strong" className="txt-small" />
              </Button>
            )}
          {!isTecalor && !privacyPolicyHidden
            && (
              <Button
                type="button"
                mode="text-link"
                onClick={() => navigate('/privacy-policy')}
                data-testid="privacy-policy-button"
                disableAutoCapture
              >
                <Translated
                  id="page-footer.privacy-policy"
                  tagName="strong"
                  className="txt-small"
                />
              </Button>
            )}
        </div>
      </div>
      <div className="align-items-center page-footer__buttons">
        {!!openTour && (
          <Button
            type="button"
            mode="text-link"
            onClick={openTour}
            data-testid="footerOpenTourButton"
          >
            <div className="align-items-center">
              <Box mr={2} className="align-items-center">
                <Icon component={IconFlag} />
              </Box>
              <Translated id="tour.open" tagName="strong" className="txt-small" />
            </div>
          </Button>
        )}
        {!!openFeedback && (
          <Button
            type="button"
            mode="text-link"
            onClick={openFeedback}
            data-testid="footerFeedBackButton"
          >
            <div className="align-items-center">
              <Box mr={2} className="align-items-center">
                <Icon component={IconMail} />
              </Box>
              <Translated id="feedback.open" tagName="strong" className="txt-small" />
            </div>
          </Button>
        )}
        {!!openSupport && (
          <Button
            type="button"
            mode="text-link"
            onClick={openSupport}
            data-testid="footerSupportButton"
          >
            <div className="align-items-center">
              <Box mr={2} className="align-items-center">
                <Icon component={IconMail} />
              </Box>
              <Translated id="support.open" tagName="strong" className="txt-small" />
            </div>
          </Button>
        )}
      </div>
    </PageFooterWrapper>
  );
}

export default PageFooter;
